import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { ALL_STAGES, STAGE_PRESETS, StagePresetKey } from "@/utils/deduction_age"
import { format_deduction_status_label } from "@/utils/deduction_status_labels"
import { split_filter_list_input } from "@/utils/filters"
import { classes } from "@/utils"

/** Order-independent equality; `null` means "all stages" (no restriction). */
export function stages_equal(a: string[] | null, b: string[] | null): boolean {
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  if (a.length !== b.length) return false
  const set_b = new Set(b)
  return a.every(s => set_b.has(s))
}

/** Which STAGE_PRESETS entry matches `stages`, if any. */
export function matching_stage_preset(stages: string[] | null): StagePresetKey | null {
  for (const [key, preset] of Object.entries(STAGE_PRESETS) as [StagePresetKey, (typeof STAGE_PRESETS)[StagePresetKey]][]) {
    if (stages_equal(stages, preset.stages)) return key
  }
  return null
}

/** Trigger / summary label for a stage multi-select selection. */
export function stage_scope_label(selected: string[] | null): string {
  const preset = matching_stage_preset(selected)
  if (preset) return STAGE_PRESETS[preset].label
  if (selected?.length === 1) return format_deduction_status_label(selected[0])
  if (selected) return `${selected.length} stages selected`
  return STAGE_PRESETS.all.label
}

/** Parse a TanStack `status_value` column filter (comma-joined stages, or cleared). */
export function parse_status_column_filter(value: unknown): string[] | null {
  if (value == null || value === "" || value === "all") return null
  if (typeof value !== "string" && typeof value !== "number") return null
  const tokens = split_filter_list_input(value)
  return tokens.length === 0 ? null : tokens
}

/** Serialize stage scope for the `status_value` column filter (`undefined` = cleared / all). */
export function serialize_status_column_filter(stages: string[] | null): string | undefined {
  return stages == null ? undefined : stages.join(",")
}

export interface StageMultiSelectProps {
  /** null = all stages (no restriction). */
  selected: string[] | null
  onChange: (stages: string[] | null) => void
  className?: string
}

/**
 * Shared stage multi-select: preset quick-filters + checkbox list.
 * Used by the Aging panel stage scope and the deductions/transactions Stage column header.
 */
export function StageMultiSelect({ selected, onChange, className }: StageMultiSelectProps) {
  const active_preset = matching_stage_preset(selected)

  function apply_preset(key: StagePresetKey) {
    onChange(STAGE_PRESETS[key].stages)
  }

  function toggle_stage(stage: string, checked: boolean) {
    const current = selected ?? ALL_STAGES
    const next = checked ? [...new Set([...current, stage])] : current.filter(v => v !== stage)
    const matched = matching_stage_preset(next)
    onChange(matched ? STAGE_PRESETS[matched].stages : next)
  }

  return (
    <div class={classes("w-72", className)}>
      <div class="mb-3 flex flex-wrap gap-1">
        {Object.entries(STAGE_PRESETS).map(([key, preset]) => (
          <Button
            key={key}
            variant={active_preset === key ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => apply_preset(key as StagePresetKey)}>
            {preset.label}
          </Button>
        ))}
      </div>
      <div class="max-h-56 space-y-1 overflow-auto">
        {ALL_STAGES.map(key => (
          <Label key={key} className="flex items-center gap-2 py-0.5 font-normal">
            <Checkbox
              checked={selected == null || selected.includes(key)}
              onCheckedChange={(checked: boolean | string) => toggle_stage(key, !!checked)}
            />
            {format_deduction_status_label(key)}
          </Label>
        ))}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("stages_equal / matching_stage_preset", () => {
    it("treats null as all-stages", () => {
      expect(stages_equal(null, null)).toBe(true)
      expect(stages_equal(null, ["new"])).toBe(false)
      expect(matching_stage_preset(null)).toBe("all")
    })

    it("matches presets order-independently", () => {
      expect(matching_stage_preset(["backup_requested"])).toBe("open_pending_backup")
      expect(matching_stage_preset(["task_assigned", "new", "pending", "review"])).toBe("open_pending_validation")
    })

    it("returns null for custom selections", () => {
      expect(matching_stage_preset(["new", "won"])).toBeNull()
    })
  })

  describe("stage_scope_label", () => {
    it("uses preset labels when the selection matches", () => {
      expect(stage_scope_label(null)).toBe("All stages")
      expect(stage_scope_label(["backup_requested"])).toBe("Open – pending backup")
    })

    it("uses the stage label for a single custom selection", () => {
      expect(stage_scope_label(["won"])).toBe("Won")
    })

    it("counts stages for multi custom selections", () => {
      expect(stage_scope_label(["new", "won"])).toBe("2 stages selected")
    })
  })

  describe("parse/serialize status column filter", () => {
    it("round-trips comma-joined stages", () => {
      expect(parse_status_column_filter("new,review")).toEqual(["new", "review"])
      expect(serialize_status_column_filter(["new", "review"])).toBe("new,review")
    })

    it("treats cleared / all as null", () => {
      expect(parse_status_column_filter(undefined)).toBeNull()
      expect(parse_status_column_filter("all")).toBeNull()
      expect(serialize_status_column_filter(null)).toBeUndefined()
    })
  })
}
